import { ThemeProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import { Security } from "@okta/okta-react";
import { SnackbarProvider } from "notistack";
import { Provider } from "react-redux";
import { RouterProvider } from "react-router-dom";

import ErrorModal from "@/ErrorHandling/ErrorModal";
import { ErrorModalContextProvider } from "@/ErrorHandling/ErrorModalContextProvider";
import ConfirmContextProvider from "@/features/confirm/ConfirmContextProvider";
import ConfirmModal from "@/features/confirm/ConfirmModal";
import { NotificationAlert } from "@/ui-components/notifications/NotificationAlert";
import { useTranslation } from "react-i18next";
import { buildTheme } from "./mui-theme";
import { router } from "./routes";
import { store } from "./services/store";

const oktaAuth = new OktaAuth({
  issuer: `${import.meta.env.VITE_OKTA_DOMAIN}`,
  clientId: `${import.meta.env.VITE_OKTA_CLIENT_ID}`,
  redirectUri: `${window.location.origin}/login/callback`,
  postLogoutRedirectUri: window.location.origin,
  scopes: ["openid", "profile", "email", "groups"],
});

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    danger: true;
    switch: true;
    destructive: true;
  }
}

declare module "notistack" {
  interface VariantOverrides {
    alert: {
      closeNotification?: () => void;
      sub_type: "Warning" | "Information";
    };
  }
}

function App() {
  const { t, i18n } = useTranslation();

  const restoreOriginalUri = (_oktaAuth: any, originalUri: string) => {
    window.location.replace(toRelativeUrl(originalUri || "/", window.location.origin));
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <ErrorModalContextProvider>
        <SnackbarProvider
          Components={{
            alert: NotificationAlert,
          }}
        >
          <ConfirmContextProvider>
            <Provider store={store}>
              <ThemeProvider theme={buildTheme(i18n.language)}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <RouterProvider router={router} />
                  <ConfirmModal />
                  <ErrorModal />
                </LocalizationProvider>
              </ThemeProvider>
            </Provider>
          </ConfirmContextProvider>
        </SnackbarProvider>
      </ErrorModalContextProvider>
    </Security>
  );
}

export default App;
